import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../hooks/apiRequest";
import { ProjectProps, Severity, Priority, ProjectStatus } from "../types/interfaces";
import { projectKeys } from "../utils/queryKeys";

interface ProjectFilters {
  status?: ProjectStatus;
  severity?: Severity;
  priority?: Priority;
  assigneeId?: string;
  enabled?: boolean;
}

function toQueryString(filters: ProjectFilters): string {
  const params = new URLSearchParams();
  (["status", "severity", "priority", "assigneeId"] as const).forEach((k) => {
    if (filters[k]) params.set(k, String(filters[k]));
  });
  return params.toString();
}

// `filters` is optional: with none, the caller's full scoped project list is
// fetched. `enabled` lets a caller opt out (e.g. UserListPage gating the
// assigned-projects multi-select on the viewer being a manager).
export function useProjects(filters: ProjectFilters = {}) {
  const queryString = toQueryString(filters);
  const { enabled, ...keyFilters } = filters;
  return useQuery({
    queryKey: projectKeys.list(keyFilters as Record<string, string | undefined>),
    queryFn: async () =>
      (await apiRequest<ProjectProps[]>(`projects${queryString ? `?${queryString}` : ""}`)).data,
    enabled: enabled ?? true,
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: async () => (await apiRequest<ProjectProps>(`projects/${id}`)).data,
    enabled: !!id,
  });
}

export type ProjectInput = Partial<
  Pick<
    ProjectProps,
    "name" | "description" | "status" | "guideUrl" | "severity" | "priority" | "startDate" | "targetDate" | "leadId" | "assignedUserIds"
  >
>;

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: ProjectInput & { name: string }) =>
      (await apiRequest<ProjectProps>("projects", { method: "POST", body: JSON.stringify(data) })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: projectKeys.all }),
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ProjectInput }) =>
      (await apiRequest<ProjectProps>(`projects/${id}`, { method: "PUT", body: JSON.stringify(data) })).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.id) });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiRequest<void>(`projects/${id}`, { method: "DELETE" })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: projectKeys.all }),
  });
}
